import type { MantineColorScheme } from '@mantine/core';
import {
	Accordion,
	Badge,
	Button,
	Card,
	CardSection,
	ColorInput,
	colorsTuple,
	createTheme,
	Drawer,
	EmptyState,
	Indicator,
	Input,
	List,
	LoadingOverlay,
	Modal,
	Paper,
	ScrollAreaAutosize,
	SegmentedControl,
	Select,
	Switch,
	Table,
	virtualColor,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';

// Read once at startup. The width matches `@mixin smaller-than $mantine-breakpoint-sm`, where
// short forms open as bottom sheets.
const isTouchPhone =
	typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse) and (max-width: 47.99375em)').matches;

// index.html's colour scheme script must use the same fallback.
export const DEFAULT_COLOR_SCHEME: MantineColorScheme = 'auto';
export const COLOR_SCHEME_STORAGE_KEY = 'mantine-color-scheme-value';

// Changing theme styles may require a full restart
export const theme = createTheme({
	primaryColor: 'app-primary',
	defaultRadius: 'sm',
	colors: {
		'app-primary': [
			'#f0fcf8',
			'#e1f9f2',
			'#bef2e2',
			'#91e9ce',
			'#5fdfb8',
			'#2ed5a1',
			'#21a37b',
			'#1b8766',
			'#15694f',
			'#0f4b39',
		], // baseColor: #21a37b (anchored at shade 6)
		'app-navy-light': colorsTuple('#0a0f38'),
		'app-navy-dark': colorsTuple('#e4e6f5'),
		'app-navy': virtualColor({ name: 'app-navy', light: 'app-navy-light', dark: 'app-navy-dark' }),
		// Payment actions: navy reads as ink on light, but a filled lavender badge would lose its white label.
		'app-accent-light': colorsTuple('#0a0f38'),
		'app-accent-dark': colorsTuple('#3b46a8'),
		'app-accent': virtualColor({ name: 'app-accent', light: 'app-accent-light', dark: 'app-accent-dark' }),
		'green': [
			'#edfbed',
			'#dcf2dd',
			'#b8e4b9',
			'#91d593',
			'#70c972',
			'#5bc15e',
			'#4fbd52',
			'#3fa643',
			'#359439',
			'#28802d',
		], // baseColor: #389B3C', generate array: https://mantine.dev/colors-generator/?color=389B3C
		'red': [
			'#fcf2f2',
			'#fae6e6',
			'#f4c8c8',
			'#eca2a2',
			'#e37878',
			'#da4e4e',
			'#c92a2a',
			'#b12525',
			'#982020',
			'#7f1a1a',
		], // baseColor: #c92a2a (anchored at shade 6)
	},
	fontFamily: 'DM Sans, sans-serif',
	headings: {
		fontFamily: 'DM Sans, sans-serif',
	},
	components: {
		Accordion: Accordion.extend({
			styles: {
				item: {
					backgroundColor: 'var(--app-surface)',
					borderWidth: 1,
					borderStyle: 'solid',
					borderColor: 'var(--item-border-color)',
					borderRadius: 'var(--accordion-radius)',
				},
			},
		}),
		Badge: Badge.extend({
			defaultProps: {
				fw: 600,
			},
		}),
		Button: Button.extend({
			defaultProps: {
				variant: 'outline',
				radius: 6,
				fw: 500,
				fz: 'sm',
			},
		}),
		Card: Card.extend({
			defaultProps: {
				radius: 'md',
			},
		}),
		CardSection: CardSection.extend({}),
		ColorInput: ColorInput.extend({
			defaultProps: {
				// On a phone, typing a hex code opens the keyboard, which resizes the web view and throws
				// a bottom sheet to the top of the screen. The picker and swatches still open on tap.
				disallowInput: isTouchPhone,
			},
		}),
		DatePickerInput: DatePickerInput.extend({
			defaultProps: {
				valueFormat: 'MMM D, YYYY',
			},
		}),
		Drawer: Drawer.extend({
			defaultProps: {
				position: 'right',
			},
			styles: (_theme, { position }) => ({
				// Single source of padding: the panel itself has none, so header and
				// body each own their (matching, compact) padding. Avoids the previous
				// content+body double-padding and the see-through gap a bottom content
				// padding left under sticky footers.
				content: {
					'padding': 0,
					'paddingLeft': 'var(--safe-area-left)',
					'paddingRight': 'var(--safe-area-right)',
					// Headers and footers a drawer renders itself read these instead of the raw safe
					// area, so they inset only where the panel actually reaches the screen edge.
					'--overlay-inset-top': position === 'bottom' ? '0px' : 'var(--safe-area-top)',
					'--overlay-inset-bottom': 'var(--safe-area-bottom)',
				},
				title: {
					fontSize: '1.375rem',
					fontStyle: 'normal',
					fontWeight: 700,
					lineHeight: '140%',
					color: 'var(--mantine-color-gray-9)',
				},
				header: {
					paddingInline: 'var(--mantine-spacing-sm)',
					// The status-bar inset goes on the sticky, opaque header rather than the content,
					// so the strip behind the clock stays covered while the body scrolls under it.
					// Bottom drawers are partial-height sheets that never reach the status bar.
					paddingTop: 'calc(var(--mantine-spacing-sm) + var(--overlay-inset-top))',
					paddingBottom: 'var(--mantine-spacing-xs)',
					minHeight: 'unset',
				},
				body: {
					padding: 'var(--mantine-spacing-md)',
				},
				close: {
					marginLeft: 'auto',
				},
			}),
		}),
		EmptyState: EmptyState.extend({
			defaultProps: {
				size: 'xs',
			},
		}),
		Indicator: Indicator.extend({
			defaultProps: {
				zIndex: 10,
			},
		}),
		Input: Input.extend({
			styles: {
				input: {
					fontSize: '16px',
				},
			},
		}),
		List: List.extend({
			defaultProps: {
				listStyleType: 'none',
			},
		}),
		LoadingOverlay: LoadingOverlay.extend({
			defaultProps: {
				overlayProps: { blur: 2 },
			},
		}),
		Modal: Modal.extend({
			defaultProps: {
				centered: true,
				radius: 'md',
				// Standardized with Drawer: compact, consistent padding across all
				// modals and drawers.
				padding: 'md',
				shadow: 'xl',
			},
			// Mantine's 5dvh is shorter than the iOS status bar, so a tall centered modal reached under it.
			vars: () => ({
				root: { '--modal-y-offset': 'max(5dvh, calc(var(--safe-area-top) + var(--mantine-spacing-xs)))' },
			}),
			// Same scheme as Drawer: a fullscreen modal's content box is its scroller, so the status-bar
			// inset goes on the sticky header, where it stays opaque while the body scrolls under it.
			// `--overlay-inset-*` come from safe-area.css: a class has to be able to set them, as the
			// bottom-sheet presentation does, and inline theme styles would outrank it.
			styles: (_theme, { fullScreen }) => ({
				header: fullScreen
					? { paddingTop: 'calc(var(--mb-padding, var(--mantine-spacing-md)) + var(--overlay-inset-top))' }
					: {},
				title: {
					fontSize: '1.375rem',
					fontStyle: 'normal',
					fontWeight: 700,
					lineHeight: '140%',
					color: 'var(--mantine-color-gray-9)',
				},
			}),
		}),
		Paper: Paper.extend({
			defaultProps: {
				withBorder: true,
				style: {
					padding: '1.5em',
					marginTop: '1em',
					marginBottom: '1em',
					boxShadow: 'none',
				},
			},
		}),
		ScrollAreaAutosize: ScrollAreaAutosize.extend({
			defaultProps: {
				scrollbarSize: 4,
			},
		}),
		SegmentedControl: SegmentedControl.extend({
			defaultProps: {
				radius: 'xl',
				withItemsBorders: false,
				variant: 'default',
			},
			styles: {
				root: {
					gap: '0.625em',
				},
				label: {
					padding: 'calc(0.325rem * var(--mantine-scale)) calc(0.855rem * var(--mantine-scale))',
				},
			},
		}),
		Select: Select.extend({
			defaultProps: {
				withCheckIcon: false,
			},
		}),
		Switch: Switch.extend({
			defaultProps: {
				thumbIcon: <></>,
			},
		}),
		Table: Table.extend({
			styles: (_theme, _props, _ctx) => ({
				table: {},
				th: {},
				td: {},
			}),
			vars: (_theme, _props, _ctx) => ({
				table: {
					'--table-vertical-spacing': '0.7rem',
				},
			}),
		}),
	},
});
